/* eslint-disable @typescript-eslint/no-explicit-any */
import dayjs from 'dayjs';

import type { OrderAppCtaType } from '@/graphql/orderApp/api';
import type { CountryCode } from '@/graphql/saleor/api';

// https://breitling.atlassian.net/wiki/spaces/BEC/pages/3672473602/V12+Financing+for+the+United+Kingdom#Link-to-existing-internal-documentation

export const PRICE_UPPER_BOUND = 27777;
export const supportedCountry: CountryCode[] = ['GB'];

export function isV12Available(
  amount: number,
  country: CountryCode,
  variantCTA?: OrderAppCtaType,
  minAmount = 0,
  maxAmount = 0,
) {
  return (
    amount >= minAmount &&
    amount <= maxAmount &&
    supportedCountry?.includes(country) &&
    variantCTA === 'ORDER'
  );
}

// Deposit Percentage limit based on the design
const minDepositPercentage = 10;
const maxDepositPercentage = 50;

export function calcMinDepositPercentage(
  amount: number,
  maxLoan: number,
): number | null {
  if (!amount || !maxLoan) return null;
  const raw = 100 - (100 * maxLoan) / amount;
  const percent = Math.ceil(raw);
  const bounded = Math.max(minDepositPercentage, percent);
  return bounded;
}

export function calcMaxDepositPercentage(
  amount: number,
  minLoan: number,
): number | null {
  if (!amount || !minLoan) return null;

  const raw = 100 - (100 * minLoan) / amount;
  const percent = Math.floor(raw);
  const bounded = Math.min(maxDepositPercentage, percent);
  return bounded;
}

const financeProducts = [
  {
    productId: 10,
    name: 'Interest Free Finance (10 Months)',
    minLoan: 2200.0,
    maxLoan: 25000.0,
    months: 10,
    monthlyRate: 0.0,
    apr: 0.0,
    serviceFee: 0.0,
    documentFee: 0.0,
    documentFeeCollectionMonth: 0,
    documentFeeMaximum: 0,
    documentFeeMinimum: 0,
    documentFeePercentage: 0.0,
    optionPeriod: 0,
    deferredPeriod: 0,
    settlementFee: 0.0,
  },
  {
    productId: 12,
    name: 'Interest Free Finance (12 Months)',
    minLoan: 2200.0,
    maxLoan: 25000.0,
    months: 12,
    monthlyRate: 0.0,
    apr: 0.0,
    serviceFee: 0.0,
    documentFee: 0.0,
    documentFeeCollectionMonth: 0,
    documentFeeMaximum: 0,
    documentFeeMinimum: 0,
    documentFeePercentage: 0.0,
    optionPeriod: 0,
    deferredPeriod: 0,
    settlementFee: 0.0,
  },
  {
    productId: 24,
    name: 'Interest Free Finance (24 Months)',
    minLoan: 2200.0,
    maxLoan: 25000.0,
    months: 24,
    monthlyRate: 0.0,
    apr: 0.0,
    serviceFee: 0.0,
    documentFee: 0.0,
    documentFeeCollectionMonth: 0,
    documentFeeMaximum: 0,
    documentFeeMinimum: 0,
    documentFeePercentage: 0.0,
    optionPeriod: 0,
    deferredPeriod: 0,
    settlementFee: 0.0,
  },
  {
    productId: 36,
    name: 'Interest Free Finance (36 Months)',
    minLoan: 2200.0,
    maxLoan: 25000.0,
    months: 36,
    monthlyRate: 0.0,
    apr: 0.0,
    serviceFee: 0.0,
    documentFee: 0.0,
    documentFeeCollectionMonth: 0,
    documentFeeMaximum: 0,
    documentFeeMinimum: 0,
    documentFeePercentage: 0.0,
    optionPeriod: 0,
    deferredPeriod: 0,
    settlementFee: 0.0,
  },
];

export const getFinanceProduct = function (id: number) {
  for (const product of financeProducts) {
    if (product.productId === id) {
      return product;
    }
  }
  return null;
};

export const getFinanceProducts = function () {
  return financeProducts;
};

export const calculateApr = function (
  loan: number,
  instalment: number,
  deferred: number,
  term: number,
) {
  let result = parseFloat(0 as any);
  let high = parseFloat(200 as any);
  let low = parseFloat(0 as any);
  let n, x, j, q, y, z;

  if (deferred > 1) {
    n = term + deferred + 1;
  } else {
    n = term + 1;
  }

  x = 1;

  while (x < 20) {
    result = (high + low) / 2;

    j = parseFloat(Math.pow(1.0 + result / 100.0, 1.0 / 12.0) as any);

    q = parseFloat((1.0 / j) as any);

    if (deferred < 1) {
      y = parseFloat(
        ((instalment * (1.0 - Math.pow(q, n))) / (1 - q) - instalment) as any,
      );

      z = parseFloat(0.0 as any);
    } else {
      y = parseFloat(
        ((instalment * (1.0 - Math.pow(q, n - 1))) / (1 - q) -
          instalment) as any,
      );

      z = parseFloat(
        ((instalment * (1.0 - Math.pow(q, deferred))) / (1 - q) -
          instalment) as any,
      );
    }

    if (y - z < loan) {
      high = result;
    } else {
      low = result;
    }

    x++;
  }

  return result;
};

const XIRR = function (
  values: number[],
  dates: (Date | dayjs.Dayjs)[],
  guess: number,
) {
  // Credits: algorithm inspired by Apache OpenOffice

  // Calculates the resulting amount
  const irrResult = function (
    values: number[],
    dates: (Date | dayjs.Dayjs)[],
    rate: number,
  ) {
    const r = rate + 1;
    let result = values[0];
    for (let i = 1; i < values.length; i++) {
      result +=
        values[i] /
        Math.pow(r, dayjs(dates[i]).diff(dayjs(dates[0]), 'days') / 365);
    }
    return result;
  };

  // Calculates the first derivation
  const irrResultDeriv = function (
    values: number[],
    dates: (Date | dayjs.Dayjs)[],
    rate: number,
  ) {
    const r = rate + 1;
    let result = 0;
    for (let i = 1; i < values.length; i++) {
      const frac = dayjs(dates[i]).diff(dayjs(dates[0]), 'days') / 365;
      result -= (frac * values[i]) / Math.pow(r, frac + 1);
    }
    return result;
  };

  // Check that values contains at least one positive value and one negative value
  let positive = false;
  let negative = false;
  for (const value of values) {
    if (value > 0) positive = true;
    if (value < 0) negative = true;
  }

  // Return error if values does not contain at least one positive value and one negative value
  if (!positive || !negative) return '#NUM!';

  // Initialize guess and resultRate
  const finalGuess = typeof guess === 'undefined' ? 0.1 : guess;
  let resultRate = finalGuess;

  // Set maximum epsilon for end of iteration
  const epsMax = 1e-10;

  // Set maximum number of iterations
  const iterMax = 50;

  // Implement Newton's method
  let newRate, epsRate, resultValue;
  let iteration = 0;
  let contLoop = true;
  do {
    resultValue = irrResult(values, dates, resultRate);
    newRate =
      resultRate - resultValue / irrResultDeriv(values, dates, resultRate);
    epsRate = Math.abs(newRate - resultRate);
    resultRate = newRate;
    contLoop = epsRate > epsMax && Math.abs(resultValue) > epsMax;
  } while (contLoop && ++iteration < iterMax);

  if (contLoop) return '#NUM!';

  // Return internal rate of return
  return resultRate;
};

const calculateAprFromIrr = function (
  loan: number,
  monthlyinstalment: number,
  loanTerm: number,
  documentfee: number,
  documentfeecollectionmonth: number,
) {
  const startDate = new Date();
  const incomeTable = [];
  const dateTable = [];

  if (documentfeecollectionmonth == 0) {
    incomeTable.push(parseFloat((loan * -1) as any) + documentfee);
  } else {
    incomeTable.push(parseFloat((loan * -1) as any));
  }
  dateTable.push(startDate);

  for (let i = 1; i <= loanTerm; i++) {
    const nextDate = dayjs(startDate).add(i, 'M');
    dateTable.push(nextDate);

    if (i - 1 == documentfeecollectionmonth && documentfeecollectionmonth > 0) {
      incomeTable.push(parseFloat((monthlyinstalment + documentfee) as any));
    } else {
      incomeTable.push(parseFloat(monthlyinstalment as any));
    }
  }
  const r = XIRR(incomeTable, dateTable, 0.1) as number;

  return Math.round(r * 10000) / 100;
};

//Calculates all financial parameters
export const calculate = function (
  financeProduct: any,
  cashPrice: number,
  deposit: number,
) {
  const apr = parseFloat(financeProduct.apr);
  const monthlyrate = parseFloat(financeProduct.monthlyRate);
  let calculatedApr;
  const months = parseFloat(financeProduct.months);
  const serviceFee = parseFloat(financeProduct.serviceFee);
  let documentFee = 0;

  cashPrice = parseFloat(cashPrice as any);

  deposit = parseFloat(deposit as any);

  const loanAmount = cashPrice - deposit;
  let initialPayments, finalPayment;

  let balancePayable = loanAmount;

  documentFee =
    financeProduct.documentFee +
    loanAmount * financeProduct.documentFeePercentage;

  if (
    financeProduct.documentFeeMinimum > 0 &&
    documentFee < financeProduct.documentFeeMinimum
  ) {
    documentFee = financeProduct.documentFeeMinimum;
  }
  if (
    financeProduct.documentFeeMaximum > 0 &&
    documentFee > financeProduct.documentFeeMaximum
  ) {
    documentFee = financeProduct.documentFeeMaximum;
  }

  if (monthlyrate == 0) {
    initialPayments = Math.round((loanAmount / months) * 100) / 100;

    if (initialPayments * months < loanAmount) {
      initialPayments += 0.01;
    }

    finalPayment = loanAmount - initialPayments * (months - 1);

    calculatedApr = 0;
  } else {
    const yieldF = Math.pow(apr / 100 + 1, 1.0 / 12);

    let pv = loanAmount - serviceFee;

    if (financeProduct.deferredPeriod > 1) {
      pv = pv * Math.pow(yieldF, financeProduct.deferredPeriod - 1);
    }

    initialPayments =
      Math.ceil(
        (0 - pv / ((Math.pow(yieldF, 0 - months) - 1) / (yieldF - 1))) * 100,
      ) / 100;

    finalPayment = initialPayments;

    balancePayable = initialPayments * months;

    calculatedApr = calculateApr(
      loanAmount - financeProduct.serviceFee,
      initialPayments,
      financeProduct.deferredPeriod,
      months,
    );
  }

  if (documentFee > 0) {
    calculatedApr = calculateAprFromIrr(
      loanAmount,
      initialPayments,
      months,
      parseFloat(documentFee as any),
      parseFloat(financeProduct.documentFeeCollectionMonth),
    );
  }

  balancePayable = initialPayments * (months - 1);
  balancePayable += finalPayment;

  const interest = balancePayable - loanAmount;

  const chargeForCredit = interest + serviceFee + documentFee;

  const amountPayable = balancePayable + serviceFee + documentFee + deposit;

  let productAvailable = true;
  let availabilityReason = '';

  if (loanAmount < financeProduct.minLoan) {
    productAvailable = false;

    availabilityReason =
      'Only available on loan amounts over £' +
      financeProduct.minLoan.toFixed(2);
  } else if (loanAmount > financeProduct.maxLoan) {
    productAvailable = false;

    availabilityReason =
      'Only available on loan amounts under £' +
      financeProduct.maxLoan.toFixed(2);
  }

  const annualRate =
    ((interest / loanAmount) * 100) /
    ((months + financeProduct.deferredPeriod) / 12);

  const financeCalculation = {
    initialPayments: initialPayments.toFixed(2),
    finalPayment: finalPayment.toFixed(2),
    balancePayable: balancePayable.toFixed(2),
    interest: interest.toFixed(2),
    chargeForCredit: chargeForCredit.toFixed(2),
    amountPayable: amountPayable.toFixed(2),
    cashPrice: cashPrice.toFixed(2),
    deposit: deposit.toFixed(2),
    loanAmount: loanAmount.toFixed(2),
    months: months,
    monthsDeferred: financeProduct.deferredPeriod,
    apr: calculatedApr.toFixed(2),
    productAvailable: productAvailable,
    availabilityReason: availabilityReason,
    productId: financeProduct.productId,
    name: financeProduct.name,
    settlementFee: financeProduct.settlementFee.toFixed(2),
    serviceFee: serviceFee.toFixed(2),
    documentFee: documentFee.toFixed(2),
    documentFeeMinimum: financeProduct.documentFeeMinimum,
    documentFeeMaximum: financeProduct.documentFeeMaximum,
    documentFeeCollectionMonth: financeProduct.documentFeeCollectionMonth,
    documentFeePercentage: financeProduct.documentFeePercentage,
    annualRate: annualRate.toFixed(2),
  };

  return financeCalculation;
};
